import React, { useState, useEffect, useRef } from 'react';
import api from './api/axiosInstance';
import Sidebar from './Sidebar.js';
import { Card, Row, Col, Container, Button, Modal, Alert, Image, Form } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import CitySalary from './CitySalary';
import { TicketCountContext } from './TicketCountContext';
import Breadcrumb from './Breadcrumb.js';
import CityGeneralInfo from './CityGeneralInfo.js';
import NavTabs from './NavTabs';
import icSucess from './assets/img/ic-celebration-circle.svg';
import { exportCityPdf } from './api/exportCityPdf.js';
import currencies from './data/currencies.js';

const CityEditSalary = () => {
    const { id } = useParams();

    const [errors, setErrors] = useState({}); // State for validation errors
    const [loading, setLoading] = useState(true); // ✅ Show spinner while fetching
    const [showDeleteModal, setShowDeleteModal] = useState(false); // Delete modal state
    const [showSavedModal, setShowSavedModal] = useState(false);
    const navigate = useNavigate();
    const { ticketCount } = React.useContext(TicketCountContext);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const salaryRef = useRef(null);
    const [childHasErrors, setChildHasErrors] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        country: {},
        url: '',
        currency: '',
        govUrl: '',
        legalUrl: '',
        remarks: '',
        centralFrom: '',
        centralTo: '',
        productionFrom: '',
        productionTo: '',
        itFrom: '',
        itTo: '',
        internCentralFrom: '',
        internCentralTo: '',
        internProductionFrom: '',
        internProductionTo: '',
        internItFrom: '',
        internItTo: '',
        oneThreeCentralFrom: '',
        oneThreeCentralTo: '',
        oneThreeProductionFrom: '',
        oneThreeProductionTo: '',
        oneThreeItFrom: '',
        oneThreeItTo: '',
        threeCentralFrom: '',
        threeCentralTo: '',
        threeProductionFrom: '',
        threeProductionTo: '',
        threeItFrom: '',
        threeItTo: '',
        upliftCentral: '',
        upliftProduction: '',
        upliftIt: '',
    });

    const userNavItems = [
        { to: '/city-edit/:id', eventKey: 'cityInfo', label: 'About' },
        { to: '/city-edit-companies/:id', eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`/city-edit-jobs/${id}`] },
        { to: '/city-edit-salary/:id', eventKey: 'salariesInfo', label: 'Average salaries' },
        { to: '/city-edit-insights/:id', eventKey: 'cityInsights', label: 'City insights' },
        { to: '/city-edit-universities/:id', eventKey: 'universities', label: 'Universities' },
        { to: '#', eventKey: 'cityComments', label: 'Comments' },
        { to: '/city-edit-history/:id', eventKey: 'history', label: 'History of changes' },
    ];

    useEffect(() => {
        const toStr = (v) => (v === 0 || typeof v === 'number' || typeof v === 'string') ? String(v) : '';

        const fetchCity = async () => {
            try {
            const res = await api.get(`/api/cities/${id}`);
            const data = res.data || {};

            const student    = Array.isArray(data.studentJobs) ? data.studentJobs[0] : {};
            const intern     = Array.isArray(data.internJobs) ? data.internJobs[0] : {};
            const oneThree   = Array.isArray(data.oneThreeJobs) ? data.oneThreeJobs[0] : {};
            const three      = Array.isArray(data.threeJobs) ? data.threeJobs[0] : {};
            const uplift     = Array.isArray(data.uplift) ? data.uplift[0] : {};

            setFormData({
                // meta
                name: data.name || '',
                country: (data.country && typeof data.country === 'object') ? data.country : {},
                url: data.url || '',
                currency: data.currency || '',
                govUrl: data.govUrl || '',
                legalUrl: data.legalUrl || '',
                remarks: data.remarks || '',

                // student (no prefix in field names)
                centralFrom:        toStr(student?.centralFrom),
                centralTo:          toStr(student?.centralTo),
                productionFrom:     toStr(student?.productionFrom),
                productionTo:       toStr(student?.productionTo),
                itFrom:             toStr(student?.itFrom),
                itTo:               toStr(student?.itTo),

                // intern
                internCentralFrom:      toStr(intern?.internCentralFrom),
                internCentralTo:        toStr(intern?.internCentralTo),
                internProductionFrom:   toStr(intern?.internProductionFrom),
                internProductionTo:     toStr(intern?.internProductionTo),
                internItFrom:           toStr(intern?.internItFrom),
                internItTo:             toStr(intern?.internItTo),

                // 1–3
                oneThreeCentralFrom:        toStr(oneThree?.oneThreeCentralFrom),
                oneThreeCentralTo:          toStr(oneThree?.oneThreeCentralTo),
                oneThreeProductionFrom:     toStr(oneThree?.oneThreeProductionFrom),
                oneThreeProductionTo:       toStr(oneThree?.oneThreeProductionTo),
                oneThreeItFrom:             toStr(oneThree?.oneThreeItFrom),
                oneThreeItTo:               toStr(oneThree?.oneThreeItTo),

                // 3+
                threeCentralFrom:       toStr(three?.threeCentralFrom),
                threeCentralTo:         toStr(three?.threeCentralTo),
                threeProductionFrom:    toStr(three?.threeProductionFrom),
                threeProductionTo:      toStr(three?.threeProductionTo),
                threeItFrom:            toStr(three?.threeItFrom),
                threeItTo:              toStr(three?.threeItTo),

                // uplift
                upliftCentral:      toStr(uplift?.upliftCentral),
                upliftProduction:   toStr(uplift?.upliftProduction),
                upliftIt:           toStr(uplift?.upliftIt),
            });

            setLoading(false);
            } catch (err) {
            console.error('Error fetching city:', err);
            setLoading(false);
            }
        };

        fetchCity();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        console.log('handleChange called with:', name, value);
    
        // Check if the incoming change is for the country object and handle accordingly
        if (name === 'country' && typeof value === 'object') {
            setFormData(prevData => ({
                ...prevData,
                country: { ...value }  // Update the country object properly
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                [name]: value  // This assumes value is always correct
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const ok = await salaryRef.current?.validateNow?.();
        
        const parentHasErrors = !!Object.values(errors || {}).some(Boolean);
        
        if (!ok || childHasErrors || parentHasErrors) {
            setShowErrorMessage(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return; // stop submit
        }
    
        const {
            // student
            centralFrom, centralTo, productionFrom, productionTo, itFrom, itTo,
            // intern
            internCentralFrom, internCentralTo, internProductionFrom, internProductionTo, internItFrom, internItTo,
            // 1–3 years
            oneThreeCentralFrom, oneThreeCentralTo, oneThreeProductionFrom, oneThreeProductionTo, oneThreeItFrom, oneThreeItTo,
            // 3+ years
            threeCentralFrom, threeCentralTo, threeProductionFrom, threeProductionTo, threeItFrom, threeItTo,
            // uplift
            upliftCentral, upliftProduction, upliftIt,
            ...restFormData
        } = formData;
    
        const payload = {
            ...restFormData,
            studentJobs: [
                {
                    centralFrom: Number(centralFrom) || 0,
                    centralTo: Number(centralTo) || 0,
                    productionFrom: Number(productionFrom) || 0,
                    productionTo: Number(productionTo) || 0,
                    itFrom: Number(itFrom) || 0,
                    itTo: Number(itTo) || 0,
                }
            ],
            internJobs: [
                {
                    internCentralFrom: Number(internCentralFrom) || 0,
                    internCentralTo: Number(internCentralTo) || 0,
                    internProductionFrom: Number(internProductionFrom) || 0,
                    internProductionTo: Number(internProductionTo) || 0,
                    internItFrom: Number(internItFrom) || 0,
                    internItTo: Number(internItTo) || 0,
                }
            ],
            oneThreeJobs: [
                {
                    oneThreeCentralFrom: Number(oneThreeCentralFrom) || 0,
                    oneThreeCentralTo: Number(oneThreeCentralTo) || 0,
                    oneThreeProductionFrom: Number(oneThreeProductionFrom) || 0,
                    oneThreeProductionTo: Number(oneThreeProductionTo) || 0,
                    oneThreeItFrom: Number(oneThreeItFrom) || 0,
                    oneThreeItTo: Number(oneThreeItTo) || 0,
                }
            ],
            threeJobs: [
                {
                    threeCentralFrom: Number(threeCentralFrom) || 0,
                    threeCentralTo: Number(threeCentralTo) || 0,
                    threeProductionFrom: Number(threeProductionFrom) || 0,
                    threeProductionTo: Number(threeProductionTo) || 0,
                    threeItFrom: Number(threeItFrom) || 0,
                    threeItTo: Number(threeItTo) || 0,
                }
            ],
            uplift: [
                {
                    upliftCentral: Number(upliftCentral) || 0,
                    upliftProduction: Number(upliftProduction) || 0,
                    upliftIt: Number(upliftIt) || 0,
                }
            ]
        };

        console.log('payload:', payload);           // in handleSubmit
    
        try {
            const response = await api.put(`/api/cities/${id}`, payload );
            setShowSavedModal(true);
            console.log('Updated city:', response.data);
        } catch (error) {
            console.error('Error updating city:', error);
        }
    };


    const handleExportPdf = async () => {
        try {
            await exportCityPdf(id);
        } catch (error) {
            console.error('Error exporting city PDF:', error);
            alert('An error occurred while exporting the city PDF.');
        }
    };

    const handleDeleteCity = () => {
        setShowDeleteModal(true);
    };

    const confirmDeleteCity = async () => {
        try {
            await api.delete(`/api/cities/${id}`);
            setShowDeleteModal(false);
            navigate('/onboarding-city-list'); // Redirect to company list after deletion
        } catch (error) {
            console.error('Error deleting city:', error);
            alert('An error occurred while deleting the city.');
        }
    };

    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            {showErrorMessage && (
                                <Alert variant="danger" onClose={() => setShowErrorMessage(false)} dismissible>
                                    <p><strong>Error</strong> - There are items that require your attention</p>
                                </Alert>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={formData.name} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <CityGeneralInfo
                                                            formData={formData}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={userNavItems} />
                                                    </Col>
                                                </Row>
                                                <Form onSubmit={handleSubmit}>
                                                    <Row>
                                                        <Col>
                                                            {!loading && 
                                                                <CitySalary
                                                                    formData={formData}
                                                                    handleChange={handleChange}
                                                                    errors={errors}
                                                                    isEditPage={true}
                                                                    handleSubmit={handleSubmit}
                                                                    onValidationChange={setChildHasErrors}
                                                                    ref={salaryRef}
                                                                    onShowErrorMessage={setShowErrorMessage}
                                                                />
                                                            }
                                                        </Col>
                                                    </Row>
                                                </Form>
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Button variant="outline-secondary" className="ms-3" onClick={() => navigate('/onboarding-city-list')}>
                                Cancel
                            </Button>
                            <Button variant="outline-danger" onClick={handleDeleteCity}>
                                Delete City
                            </Button>
                            <Button variant="outline-secondary" onClick={handleExportPdf}>
                                Download city report
                            </Button>
                        </Col>
                    </Row>
                
                </Container>
            </main>
                                    
            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this city? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="outline-danger" onClick={confirmDeleteCity}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Saved info Modal */}
            <Modal show={showSavedModal} onHide={() => setShowSavedModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">Update successful</h2>
                    <p className="text-center">City Salary Info Updated</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowSavedModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default CityEditSalary;
